import { apiFetch } from "@/shared/api/apiFetch";
import { readApiErrorMessage } from "@/shared/api/parseApiErrorMessage";

export type AppTabSummary = {
  id: number;
  app_id: number;
  name: string;
  sort_order: number;
  created_at: string;
  updated_at: string;
};

function isAppTabSummary(value: unknown): value is AppTabSummary {
  if (typeof value !== "object" || value === null) {
    return false;
  }
  const row = value as Record<string, unknown>;
  return (
    typeof row.id === "number" &&
    typeof row.app_id === "number" &&
    typeof row.name === "string" &&
    typeof row.sort_order === "number" &&
    typeof row.created_at === "string" &&
    typeof row.updated_at === "string"
  );
}

export async function fetchAppTabs(appId: number): Promise<AppTabSummary[]> {
  const res = await apiFetch(`/v1/apps/${appId}/tabs`, { method: "GET" });
  if (!res.ok) {
    throw new Error(await readApiErrorMessage(res));
  }
  const data: unknown = await res.json();
  if (!Array.isArray(data) || !data.every(isAppTabSummary)) {
    throw new Error("Unexpected response shape");
  }
  return data;
}

export type AddTabToAppPayload = {
  name: string;
};

export type UpdateAppTabPayload = {
  name: string;
};

export async function addTabToApp(
  appId: number,
  payload: AddTabToAppPayload,
): Promise<AppTabSummary> {
  const res = await apiFetch(`/v1/apps/${appId}/tabs`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
  if (!res.ok) {
    throw new Error(await readApiErrorMessage(res));
  }
  const data: unknown = await res.json();
  if (!isAppTabSummary(data)) {
    throw new Error("Unexpected response shape");
  }
  return data;
}

export async function updateAppTab(
  appId: number,
  tabId: number,
  payload: UpdateAppTabPayload,
): Promise<AppTabSummary> {
  const res = await apiFetch(`/v1/apps/${appId}/tabs/${tabId}`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
  if (!res.ok) {
    throw new Error(await readApiErrorMessage(res));
  }
  const data: unknown = await res.json();
  if (!isAppTabSummary(data)) {
    throw new Error("Unexpected response shape");
  }
  return data;
}

/**
 * Delete a tab. When other tabs remain, `fallbackTabId` is required — the tab's widgets
 * move to it. When deleting the last tab, omit it: widgets revert to the Default Tab.
 */
export async function deleteAppTab(
  appId: number,
  tabId: number,
  fallbackTabId?: number | null,
): Promise<void> {
  const query =
    fallbackTabId === undefined || fallbackTabId === null
      ? ""
      : `?fallback_tab_id=${fallbackTabId}`;
  const res = await apiFetch(`/v1/apps/${appId}/tabs/${tabId}${query}`, { method: "DELETE" });
  if (!res.ok) {
    throw new Error(await readApiErrorMessage(res));
  }
}

export async function reorderAppTabs(appId: number, tabIds: number[]): Promise<void> {
  const res = await apiFetch(`/v1/apps/${appId}/tabs/reorder`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ tab_ids: tabIds }),
  });
  if (!res.ok) {
    throw new Error(await readApiErrorMessage(res));
  }
}
